import { isWorkReadBlocked, markWorkDirty } from '@core/session/recovery'
import { CLASH_TRIAGE_KEY } from '@core/session/recoveryManifest'
import { sanitizeTriage, temDecisao, type ClashTriageMap } from './clashTriage'
import { emptySide, type ClashSide, type ClashSideSource } from './clashSets'
import type { FilterQuery, FilterRule } from '@viewer/lib/filter'
import { lerFolgaPares, PENETRACAO_MINIMA_POR_OMISSAO } from './clashFiltroEntrada'
import type { ClashCorrida, ClashEnvioModulo } from './clashReconciliacao'
import { sanitizeUltimaExportacao, type ClashUltimaExportacao } from './clashExportacaoRegisto'
import {
  classificacaoVazia,
  lerClassificacao,
  temClassificacao,
  type ClashClassificacaoFile,
} from './clashAgrupamentoEstado'

// ---------------------------------------------------------------------------
// Persistencia da triagem dos conflitos.
//
// Mesmo contrato das vistas 2D (beta.75): isto e TRABALHO, por isso
// escreve-se no buffer de recuperacao (localStorage + `markWorkDirty`) e
// NAO se le enquanto o arranque nao decidir se recupera ou descarta
// (`isWorkReadBlocked`). Os RESULTADOS da deteccao nao passam por aqui:
// sao derivados do modelo e da configuracao e ficam em memoria, senao
// reapareciam sozinhos depois de um reload, que e exactamente o que a
// rede de recuperacao proibe.
//
// Um ficheiro so, indexado pelo `clashKey` (que ja tras o modelo la
// dentro). Nao se reparte por modelo porque um conflito e, por
// definicao, entre dois modelos.
//
// TRABALHO COM A PERGUNTA DA RECUPERACAO POR RESPONDER. Enquanto o aviso
// "recuperar ou descartar" esta adiado, a triagem anterior nao se le (a
// decisao pode ser descartar) nem se escreve por cima (foi assim que se
// perderam as 1186 entradas do Cha139). O que se faz nesse intervalo vai
// para uma chave a parte, `vn2r.clash.triage.pendente`, com o mesmo
// formato. Logo que a decisao esta tomada, a primeira leitura ou escrita
// junta-a a triagem: "Recuperar" junta o novo por cima do anterior,
// "Descartar" fica so com o novo (o descartar apagou o anterior; a chave
// pendente nao e chave do buffer, por isso fica). Se o separador fechar
// sem decisao, a chave pendente fica no localStorage e junta-se na
// abertura seguinte, depois da decisao dessa vez.
// ---------------------------------------------------------------------------

/** Trabalho feito com a decisao da recuperacao por tomar. */
export const CLASH_TRIAGE_PENDENTE_KEY = 'vn2r.clash.triage.pendente'

/** A chave onde se le e escreve agora: a pendente enquanto a leitura
 *  esta bloqueada, a da triagem depois. */
function chaveActiva(): string {
  return isWorkReadBlocked() ? CLASH_TRIAGE_PENDENTE_KEY : CLASH_TRIAGE_KEY
}

export const CLASH_TRIAGE_STORE_VERSION = 1

/**
 * Configuracao do teste, guardada com a triagem.
 *
 * Porque e que isto passou a ser TRABALHO e nao preferencia: desde que um
 * lado pode ser uma REGRA (o construtor de filtros), a configuracao
 * deixou de ser "dois selectores que se refazem em segundos" e passou a
 * ser o enunciado do teste, escrito a mao por quem analisa. Perde-lo num
 * reload e perder trabalho. Vai no MESMO ficheiro da triagem
 * (`vn2r.clash.triage`), de proposito: essa chave ja e chave de trabalho
 * conhecida da rede de recuperacao e ja viaja no ficheiro de sessao
 * (`local_work`), por isso isto entra sem tocar no manifesto.
 *
 * Os RESULTADOS continuam fora: sao derivados dos modelos e nao podem
 * reaparecer sozinhos.
 */
export interface ClashSetupFile {
  sideA: ClashSide
  sideB: ClashSide
  preset: string
  /** Metros. */
  tolerance: number
  clearance: number
  ignoreContacts: number
  selfClash: boolean
  /** Penetracao minima para uma interferencia entrar na lista, m (o
   *  filtro a entrada, clashFiltroEntrada.ts). Ausente nos ficheiros
   *  anteriores: le-se o valor de omissao. */
  penetracaoMinima: number
  /** Pares de especialidades em que se verifica a folga. */
  folgaPares: string[]
}

export interface ClashTriageFile {
  version: number
  entries: ClashTriageMap
  /** Ausente em ficheiros gravados antes de os conjuntos por regra
   *  existirem. Ausente le-se como "sem configuracao guardada". */
  setup?: ClashSetupFile
  /** Historico das corridas, o identificador do projecto (Guids BCF
   *  estaveis), as chaves da ultima corrida e os topicos enviados para o
   *  modulo BCF. Ausentes nos ficheiros anteriores a reconciliacao. */
  historico?: ClashCorrida[]
  projectoBcf?: string
  chavesUltimaCorrida?: string[]
  modulo?: ClashEnvioModulo | null
  /** Registo da ultima exportacao para ficheiro (clashExportacaoRegisto.ts).
   *  Ausente nos ficheiros anteriores. */
  ultimaExportacao?: ClashUltimaExportacao
  /** Regras de classificacao, especialidades, metodo de agrupamento e
   *  grupos (clashAgrupamentoEstado.ts). Ausente nos ficheiros
   *  anteriores. */
  classificacao?: ClashClassificacaoFile
}

/** A parte do ficheiro das corridas (tudo menos a triagem e a
 *  configuracao). */
export interface ClashCorridasFile {
  historico: ClashCorrida[]
  projectoBcf: string | null
  chavesUltimaCorrida: string[]
  modulo: ClashEnvioModulo | null
  /** Registo da ultima exportacao para ficheiro, ou null. */
  ultimaExportacao: ClashUltimaExportacao | null
}

export function corridasVazias(): ClashCorridasFile {
  return { historico: [], projectoBcf: null, chavesUltimaCorrida: [], modulo: null, ultimaExportacao: null }
}

/** Historico mantido: as ultimas corridas chegam para comparar e para o
 *  relatorio; mais do que isto so engorda o ficheiro de sessao. */
const MAX_CORRIDAS = 50

/** Le a parte das corridas de um objecto ja lido (ficheiro ou sessao). */
export function sanitizeCorridas(v: unknown): ClashCorridasFile {
  const o = (v && typeof v === 'object' ? v : {}) as Record<string, unknown>
  const historico = Array.isArray(o.historico)
    ? (o.historico.filter(
        (c) =>
          c &&
          typeof c === 'object' &&
          typeof (c as { data?: unknown }).data === 'string' &&
          typeof (c as { config?: unknown }).config === 'object',
      ) as ClashCorrida[]).map((c) => ({
        ...c,
        config: {
          ...c.config,
          sideA: sanitizeSide(c.config.sideA),
          sideB: sanitizeSide(c.config.sideB),
        },
      }))
    : []
  const projecto =
    typeof o.projectoBcf === 'string' && /^[0-9a-fA-F-]{36}$/.test(o.projectoBcf) ? o.projectoBcf : null
  const chaves = Array.isArray(o.chavesUltimaCorrida)
    ? o.chavesUltimaCorrida.filter((k): k is string => typeof k === 'string')
    : []
  const m = o.modulo as Record<string, unknown> | null | undefined
  const modulo: ClashEnvioModulo | null =
    m && typeof m === 'object' && typeof m.bcfLocalId === 'string' && m.topicos && typeof m.topicos === 'object'
      ? {
          bcfLocalId: m.bcfLocalId,
          topicos: m.topicos as ClashEnvioModulo['topicos'],
          aplicadoAte: typeof m.aplicadoAte === 'string' ? m.aplicadoAte : '',
        }
      : null
  return {
    historico: historico.slice(-MAX_CORRIDAS),
    projectoBcf: projecto,
    chavesUltimaCorrida: chaves,
    modulo,
    ultimaExportacao: sanitizeUltimaExportacao(o.ultimaExportacao),
  }
}

/** Interface minima do localStorage, injectavel para os testes correrem
 *  em Node sem DOM. */
export interface KeyValueStore {
  getItem: (key: string) => string | null
  setItem: (key: string, value: string) => void
  removeItem: (key: string) => void
}

function defaultStore(): KeyValueStore | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage
  } catch {
    return null
  }
}

function readFile(raw: string | null): Partial<ClashTriageFile> | null {
  if (!raw) return null
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return null
  }
  if (!parsed || typeof parsed !== 'object') return null
  const file = parsed as Partial<ClashTriageFile>
  if (file.version !== CLASH_TRIAGE_STORE_VERSION) return null
  return file
}

export function parseTriageFile(raw: string | null): ClashTriageMap {
  const file = readFile(raw)
  if (!file) return {}
  return sanitizeTriage(file.entries)
}

// --- configuracao do teste --------------------------------------------------

function sanitizeStringList(v: unknown): string[] {
  if (!Array.isArray(v)) return []
  return v.filter((x): x is string => typeof x === 'string')
}

function sanitizeRule(v: unknown): FilterRule | null {
  if (!v || typeof v !== 'object') return null
  const r = v as Partial<FilterRule>
  if (typeof r.dimension !== 'string' || typeof r.operator !== 'string') {
    return null
  }
  return {
    id:
      typeof r.id === 'string' && r.id !== ''
        ? r.id
        : Math.random().toString(36).slice(2, 10),
    dimension: r.dimension as FilterRule['dimension'],
    operator: r.operator as FilterRule['operator'],
    values: sanitizeStringList(r.values),
    ...(typeof r.propertyKey === 'string' ? { propertyKey: r.propertyKey } : {}),
  }
}

function sanitizeQuery(v: unknown): FilterQuery | undefined {
  if (!v || typeof v !== 'object') return undefined
  const q = v as Partial<FilterQuery>
  const rules = Array.isArray(q.rules)
    ? q.rules.map(sanitizeRule).filter((r): r is FilterRule => r !== null)
    : []
  if (rules.length === 0) return undefined
  return { combinator: q.combinator === 'or' ? 'or' : 'and', rules }
}

function sanitizeSide(v: unknown): ClashSide {
  if (!v || typeof v !== 'object') return emptySide()
  const s = v as Partial<ClashSide>
  const source: ClashSideSource =
    s.source === 'selection' || s.source === 'rule' ? s.source : 'categories'
  const rule = sanitizeQuery(s.rule)
  return {
    source,
    models: sanitizeStringList(s.models),
    categories: sanitizeStringList(s.categories).map((c) => c.toUpperCase()),
    ...(rule ? { rule } : {}),
  }
}

function metres(v: unknown, fallback = 0): number {
  return typeof v === 'number' && Number.isFinite(v) && v >= 0 ? v : fallback
}

export function sanitizeSetup(v: unknown): ClashSetupFile | null {
  if (!v || typeof v !== 'object') return null
  const s = v as Partial<ClashSetupFile>
  return {
    sideA: sanitizeSide(s.sideA),
    sideB: sanitizeSide(s.sideB),
    preset: typeof s.preset === 'string' ? s.preset : '',
    tolerance: metres(s.tolerance, 0.001),
    clearance: metres(s.clearance),
    ignoreContacts: metres(s.ignoreContacts),
    selfClash: s.selfClash === true,
    penetracaoMinima: metres(s.penetracaoMinima, PENETRACAO_MINIMA_POR_OMISSAO),
    folgaPares: lerFolgaPares(s.folgaPares),
  }
}

export function parseSetupFile(raw: string | null): ClashSetupFile | null {
  const file = readFile(raw)
  if (!file) return null
  return sanitizeSetup(file.setup)
}

export function loadSetup(
  store: KeyValueStore | null = defaultStore(),
): ClashSetupFile | null {
  if (!store) return null
  if (isWorkReadBlocked()) return null
  fundirPendente(store)
  try {
    return parseSetupFile(store.getItem(CLASH_TRIAGE_KEY))
  } catch {
    return null
  }
}

/** Grava a configuracao sem tocar na triagem que la esteja. */
export function persistSetup(
  setup: ClashSetupFile | null,
  store: KeyValueStore | null = defaultStore(),
): boolean {
  if (!store) return false
  if (!isWorkReadBlocked()) fundirPendente(store)
  return writeFile(readTriageOf(store), setup, store)
}

/** Le so a parte do ficheiro que nao se vai reescrever. Um armazenamento
 *  que rebenta a leitura le-se como vazio: mais vale gravar so o que se
 *  tem do que nao gravar nada. */
function readTriageOf(store: KeyValueStore): ClashTriageMap {
  try {
    return parseTriageFile(store.getItem(chaveActiva()))
  } catch {
    return {}
  }
}

function readSetupOf(store: KeyValueStore): ClashSetupFile | null {
  try {
    return parseSetupFile(store.getItem(chaveActiva()))
  } catch {
    return null
  }
}

function readCorridasOf(store: KeyValueStore): ClashCorridasFile {
  try {
    const file = readFile(store.getItem(chaveActiva()))
    return sanitizeCorridas(file ?? {})
  } catch {
    return corridasVazias()
  }
}

function readClassificacaoOf(store: KeyValueStore): ClashClassificacaoFile {
  try {
    const file = readFile(store.getItem(chaveActiva()))
    return lerClassificacao(file?.classificacao)
  } catch {
    return classificacaoVazia()
  }
}

function temCorridas(c: ClashCorridasFile): boolean {
  return c.historico.length > 0 || c.projectoBcf !== null || c.modulo !== null || c.ultimaExportacao !== null
}

function writeFile(
  entries: ClashTriageMap,
  setup: ClashSetupFile | null,
  store: KeyValueStore,
  corridas: ClashCorridasFile = readCorridasOf(store),
  classificacao: ClashClassificacaoFile = readClassificacaoOf(store),
): boolean {
  // Enquanto a pergunta da recuperacao esta por responder, escreve-se na
  // chave pendente, nunca por cima da triagem guardada (foi o que apagou
  // as 1186 entradas do Cha139). Os valores por omissao dos argumentos
  // ja foram lidos da mesma chave (chaveActiva).
  const chave = chaveActiva()
  try {
    const comClassificacao = temClassificacao(classificacao)
    if (Object.keys(entries).length === 0 && !setup && !temCorridas(corridas) && !comClassificacao) {
      store.removeItem(chave)
    } else {
      const file: ClashTriageFile = {
        version: CLASH_TRIAGE_STORE_VERSION,
        entries,
        ...(setup ? { setup } : {}),
        ...(corridas.historico.length > 0 ? { historico: corridas.historico.slice(-MAX_CORRIDAS) } : {}),
        ...(corridas.projectoBcf ? { projectoBcf: corridas.projectoBcf } : {}),
        ...(corridas.chavesUltimaCorrida.length > 0 ? { chavesUltimaCorrida: corridas.chavesUltimaCorrida } : {}),
        ...(corridas.modulo ? { modulo: corridas.modulo } : {}),
        ...(corridas.ultimaExportacao ? { ultimaExportacao: corridas.ultimaExportacao } : {}),
        ...(comClassificacao ? { classificacao } : {}),
      }
      store.setItem(chave, JSON.stringify(file))
    }
    markWorkDirty()
    return true
  } catch {
    return false
  }
}

export function loadTriage(
  store: KeyValueStore | null = defaultStore(),
): ClashTriageMap {
  if (!store) return {}
  if (isWorkReadBlocked()) return {}
  fundirPendente(store)
  try {
    return parseTriageFile(store.getItem(CLASH_TRIAGE_KEY))
  } catch {
    return {}
  }
}

/** Grava o mapa completo, preservando a configuracao que la esteja.
 *  `true` quando conseguiu escrever. Ficheiro sem pareceres NEM
 *  configuracao apaga a chave, para o manifesto de recuperacao nao ficar
 *  a selar trabalho que ja nao existe. */
export function persistTriage(
  entries: ClashTriageMap,
  store: KeyValueStore | null = defaultStore(),
): boolean {
  if (!store) return false
  if (!isWorkReadBlocked()) fundirPendente(store)
  return writeFile(entries, readSetupOf(store), store)
}

/** Le a parte das corridas (historico, projecto, ultima corrida,
 *  modulo). Bloqueada, como a triagem, ate o arranque decidir. */
export function loadCorridas(
  store: KeyValueStore | null = defaultStore(),
): ClashCorridasFile {
  if (!store || isWorkReadBlocked()) return corridasVazias()
  fundirPendente(store)
  return readCorridasOf(store)
}

/** Grava a parte das corridas sem tocar na triagem nem na configuracao. */
export function persistCorridas(
  corridas: ClashCorridasFile,
  store: KeyValueStore | null = defaultStore(),
): boolean {
  if (!store) return false
  if (!isWorkReadBlocked()) fundirPendente(store)
  return writeFile(readTriageOf(store), readSetupOf(store), store, corridas)
}

/** Grava tudo de uma vez (repor uma sessao). Sem `classificacao`,
 *  fica a que la esta. */
export function persistTudo(
  entries: ClashTriageMap,
  setup: ClashSetupFile | null,
  corridas: ClashCorridasFile,
  store: KeyValueStore | null = defaultStore(),
  classificacao?: ClashClassificacaoFile,
): boolean {
  if (!store) return false
  if (!isWorkReadBlocked()) fundirPendente(store)
  return writeFile(entries, setup, store, corridas, classificacao ?? readClassificacaoOf(store))
}

/** Le a parte da classificacao e do agrupamento. Bloqueada, como a
 *  triagem, ate o arranque decidir. */
export function loadClassificacao(
  store: KeyValueStore | null = defaultStore(),
): ClashClassificacaoFile {
  if (!store || isWorkReadBlocked()) return classificacaoVazia()
  fundirPendente(store)
  return readClassificacaoOf(store)
}

/** Grava a triagem e a classificacao juntas (classificar, agrupar,
 *  editar grupos), sem tocar na configuracao nem nas corridas. */
export function persistTriagemEClassificacao(
  entries: ClashTriageMap,
  classificacao: ClashClassificacaoFile,
  store: KeyValueStore | null = defaultStore(),
): boolean {
  if (!store) return false
  if (!isWorkReadBlocked()) fundirPendente(store)
  return writeFile(entries, readSetupOf(store), store, readCorridasOf(store), classificacao)
}

/** As leituras de trabalho estao bloqueadas (a recuperacao ainda nao
 *  foi decidida)? Quem hidrata nao se pode dar por hidratado agora. */
export function leituraBloqueada(): boolean {
  return isWorkReadBlocked()
}

/** Quantas entradas com decisao tem a triagem guardada. */
export function decididasGuardadas(store: KeyValueStore | null = defaultStore()): number {
  if (!store) return 0
  if (!isWorkReadBlocked()) fundirPendente(store)
  return Object.values(readTriageOf(store)).filter(temDecisao).length
}

/** Ha trabalho pendente (feito com a decisao da recuperacao por tomar)? */
export function temPendente(store: KeyValueStore | null = defaultStore()): boolean {
  try {
    return !!store?.getItem(CLASH_TRIAGE_PENDENTE_KEY)
  } catch {
    return false
  }
}

/** O registo de exportacao mais recente dos dois (pela data). */
function maisRecente(
  a: ClashUltimaExportacao | null,
  b: ClashUltimaExportacao | null,
): ClashUltimaExportacao | null {
  if (!a) return b
  if (!b) return a
  return b.data >= a.data ? b : a
}

/**
 * Junta o trabalho pendente a triagem e apaga a chave pendente. So corre
 * com a decisao tomada. A triagem que la estiver (a recuperada, ou nada
 * depois de descartar) fica por baixo; o pendente e mais recente e fica
 * por cima, entrada a entrada.
 */
export function fundirPendente(store: KeyValueStore | null = defaultStore()): boolean {
  if (!store || isWorkReadBlocked()) return false
  let rawPendente: string | null
  try {
    rawPendente = store.getItem(CLASH_TRIAGE_PENDENTE_KEY)
  } catch {
    return false
  }
  if (!rawPendente) return false
  const pend = readFile(rawPendente)
  if (!pend) {
    // Ilegivel: nao se perde a triagem por causa dele, mas tambem nao se
    // apaga sem a ler; fica para quem o quiser ver.
    return false
  }
  const base = readFile(store.getItem(CLASH_TRIAGE_KEY)) ?? {}
  const entries = { ...sanitizeTriage(base.entries), ...sanitizeTriage(pend.entries) }
  const setup = sanitizeSetup(pend.setup) ?? sanitizeSetup(base.setup)
  const cb = sanitizeCorridas(base)
  const cp = sanitizeCorridas(pend)
  const porData = new Map<string, ClashCorrida>()
  for (const c of [...cb.historico, ...cp.historico]) porData.set(c.data, c)
  const corridas: ClashCorridasFile = {
    historico: [...porData.values()].sort((a, b) => a.data.localeCompare(b.data)).slice(-MAX_CORRIDAS),
    projectoBcf: cb.projectoBcf ?? cp.projectoBcf,
    chavesUltimaCorrida: cp.chavesUltimaCorrida.length > 0 ? cp.chavesUltimaCorrida : cb.chavesUltimaCorrida,
    modulo: cp.modulo ?? cb.modulo,
    ultimaExportacao: maisRecente(cb.ultimaExportacao, cp.ultimaExportacao),
  }
  const clb = lerClassificacao(base.classificacao)
  const clp = lerClassificacao(pend.classificacao)
  const classificacao = temClassificacao(clp)
    ? {
        ...clb,
        ...clp,
        especialidades: { ...clb.especialidades, ...clp.especialidades },
        grupos: { ...clb.grupos, ...clp.grupos },
      }
    : clb
  const ok = writeFile(entries, setup, store, corridas, classificacao)
  if (!ok) return false
  try {
    store.removeItem(CLASH_TRIAGE_PENDENTE_KEY)
  } catch {
    /* fica para a proxima */
  }
  return true
}

export { CLASH_TRIAGE_KEY }
